import moment from 'moment';

/**
 * The five record types a unified Home list can hold.
 *
 * The values are the server's `HomeQueueType` enum, and they arrive on every row
 * as `row.type` and as the keys of `counts_by_type`. Nothing here decides which
 * types a user sees — the payload does, and a chip is rendered only for a type
 * the payload actually counted.
 */
export const TYPE_PURCHASE_REQUISITION = 'purchase_requisition';
export const TYPE_PURCHASE_ORDER = 'purchase_order';
export const TYPE_POSITION_FUND = 'position_fund';
export const TYPE_EXPENSE_FUND = 'expense_fund';
export const TYPE_BUDGET_CHANGE = 'budget_change';

/**
 * The chip label for a type.
 *
 * Plural, because a chip labels a group; the row's own Type cell reads
 * `row.type_label`, which the server sends in the singular. Purchase orders are
 * "Expenditure Approvals" throughout the product, so the chip follows suit.
 */
export const typeChipLabel = (type) =>
    ({
        [TYPE_PURCHASE_REQUISITION]: $t('Purchase requisitions'),
        [TYPE_PURCHASE_ORDER]: $t('Expenditure approvals'),
        [TYPE_POSITION_FUND]: $t('Position fund'),
        [TYPE_EXPENSE_FUND]: $t('Expense fund'),
        [TYPE_BUDGET_CHANGE]: $t('Budget changes'),
    })[type] ?? type;

/**
 * The buckets the approvals register can be read in — the server's
 * `HomeApprovalStatus` enum, sent as `?status=`.
 */
export const STATUS_PENDING = 'pending';
export const STATUS_APPROVED_BY_ME = 'approved_by_me';
export const STATUS_REJECTED_BY_ME = 'rejected_by_me';
export const STATUS_ALL = 'all';

export const statusFilterLabel = (status) =>
    ({
        [STATUS_PENDING]: $t('Pending'),
        [STATUS_APPROVED_BY_ME]: $t('Approved by me'),
        [STATUS_REJECTED_BY_ME]: $t('Rejected by me'),
        [STATUS_ALL]: $t('All'),
    })[status] ?? status;

/**
 * What a row's own approver record says — the server's `HomeApprovalOutcome`,
 * arriving on every approvals row as `row.outcome`.
 *
 * The client never derives this. The four `approvables` types and the budget
 * change spell the same three outcomes in two different pivot shapes, and only
 * the server knows which shape a given row came from.
 */
export const OUTCOME_PENDING = 'pending';
export const OUTCOME_APPROVED = 'approved';
export const OUTCOME_REJECTED = 'rejected';

/**
 * How long a row has been waiting on this approver, in the shortest unit that
 * still says something useful.
 *
 * Days once it has been a day, hours below that, minutes below an hour — the
 * wireframe's "6 days" / "4 hrs". A row timestamped a moment into the future by
 * clock skew reads as brand new rather than as a negative wait.
 */
export const waitingLabel = (sortAt) => {
    if (!sortAt) {
        return '';
    }

    const minutes = Math.max(0, moment().diff(moment(sortAt), 'minutes'));

    if (minutes >= 24 * 60) {
        const days = Math.floor(minutes / (24 * 60));

        return $t('{{count}} days', { count: days });
    }

    if (minutes >= 60) {
        return $t('{{count}} hrs', { count: Math.floor(minutes / 60) });
    }

    return $t('{{count}} mins', { count: minutes });
};

/**
 * A React key for one register row.
 *
 * THE RECORD ID IS NOT UNIQUE HERE. A chain attaches a new approver row per
 * attempt, so one record can contribute several rows to a history or `all`
 * bucket — the same id, the user's own separate decisions.
 *
 * The server de-duplicates its union on these six fields and one more,
 * `is_returned`, which no approvals source sets and which is therefore the same
 * zero on every row this runs against. So these six are what distinguishes two
 * rows of an approvals list.
 */
export const queueRowKey = (row) => [row.type, row.id, row.sort_at, row.outcome ?? '', row.actioned_at ?? '', row.comment ?? ''].join(':');

/**
 * When the approver acted, as an absolute date.
 *
 * A history row is read months later, so a relative age ("212 days") says
 * nothing a reader can check against their own records. The wireframe's
 * "11 Sep 2026".
 */
export const actionedLabel = (actionedAt) => (actionedAt ? moment(actionedAt).format('D MMM YYYY') : '');

/**
 * What the first column of the register reads: how long a pending row has been
 * waiting, or the date an actioned one was decided.
 */
export const waitingOrActionedLabel = (row) => (row.outcome === OUTCOME_PENDING ? waitingLabel(row.sort_at) : actionedLabel(row.actioned_at));

/**
 * Whether a row has been waiting longer than the approval SLA.
 *
 * The threshold is the server's own `HomeDeltasResolver::APPROVAL_AGE_THRESHOLD_DAYS`,
 * published to the page, so the rows marked as exceptions here are the ones the
 * approvals tile counted as breached. The boundary is EXCLUSIVE, matching the
 * server: exactly three days is not yet a breach.
 */
export const isOverThreshold = (sortAt, thresholdDays) => {
    if (!sortAt || !thresholdDays) {
        return false;
    }

    return moment().diff(moment(sortAt), 'minutes') > thresholdDays * 24 * 60;
};
